<template>
  <div v-if="visibleTasks.length" class="ai-testcase-task-panel">
    <div class="ai-testcase-task-title">AI样例生成提醒</div>
    <div
      v-for="task in visibleTasks"
      :key="task.taskId"
      class="ai-testcase-task-card"
      :class="{'is-error': task.status !== 'SUCCESS'}"
      @click="navigateToTaskPage(task)"
    >
      <button
        type="button"
        class="ai-testcase-task-close"
        title="关闭提示"
        @click.stop="dismissTask(task)"
      >×</button>
      <div class="ai-testcase-task-main">
        <span class="ai-testcase-task-status">{{ task.status === 'SUCCESS' ? '已生成' : '生成失败' }}</span>
        <span class="ai-testcase-task-problem">{{ getTaskProblemLabel(task) }}</span>
      </div>
      <div class="ai-testcase-task-desc">
        {{ task.status === 'SUCCESS' ? '点击回到题目创建/编辑页查看并验收' : (task.errorMessage || '点击回到题目创建/编辑页处理') }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  AI_TESTCASE_REVIEW_TASK_ID,
  AI_TESTCASE_TASK_UPDATE_EVENT,
  dismissAiTestcaseTask,
  dispatchAiTestcaseReviewRequest,
  getAiTestcaseTaskKeys,
  getAiTestcaseTaskProblemLabel,
  isAiTestcaseTaskCanceled,
  isAiTestcaseTaskFinished,
  isAiTestcaseTaskPending,
  markAiTestcaseTaskNotified,
  normalizeAiTestcaseTask,
  readAiTestcaseTask
} from "../../common/aiTestcaseTaskStore";

export default {
  name: "AiTestcaseTaskNotifier",
  data() {
    return {
      timer: null,
      polling: {},
      visibleTasks: []
    };
  },
  mounted() {
    this.startWatcher();
    window.addEventListener(AI_TESTCASE_TASK_UPDATE_EVENT, this.handleTaskUpdated);
  },
  beforeDestroy() {
    this.clearWatcher();
    window.removeEventListener(AI_TESTCASE_TASK_UPDATE_EVENT, this.handleTaskUpdated);
  },
  watch: {
    "$route.path"() {
      this.pollTasks();
    }
  },
  methods: {
    isWatcherEnabled() {
      return String(sessionStorage.getItem("tag")) === "1";
    },
    startWatcher() {
      this.clearWatcher();
      this.pollTasks();
      this.timer = setInterval(() => {
        this.pollTasks();
      }, 5000);
    },
    clearWatcher() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    handleTaskUpdated() {
      this.refreshVisibleTasks();
      this.pollTasks();
    },
    pollTasks() {
      if (!this.isWatcherEnabled()) {
        this.visibleTasks = [];
        return;
      }
      const keys = getAiTestcaseTaskKeys();
      if (!keys.length) {
        this.visibleTasks = [];
        return;
      }
      this.refreshVisibleTasks(keys);
      keys.forEach(storageKey => {
        const cachedTask = readAiTestcaseTask(storageKey);
        if (!cachedTask || !cachedTask.taskId) {
          return;
        }
        if (!isAiTestcaseTaskPending(cachedTask.status)) {
          if (isAiTestcaseTaskFinished(cachedTask.status)
            && !isAiTestcaseTaskCanceled(cachedTask.status)
            && !cachedTask.notificationShownAt) {
            this.notifyTask(storageKey, cachedTask);
          }
          return;
        }
        if (this.polling[cachedTask.taskId]) {
          return;
        }
        this.$set(this.polling, cachedTask.taskId, true);
        this.$http
          .get("BUAAOJ/ai/testcase/result/" + cachedTask.taskId, {})
          .then(response => {
            const latestTask = readAiTestcaseTask(storageKey);
            if (!latestTask || latestTask.taskId !== cachedTask.taskId) {
              this.$delete(this.polling, cachedTask.taskId);
              this.refreshVisibleTasks();
              return;
            }
            const resultData = response.data && response.data.data ? response.data.data : {};
            const task = normalizeAiTestcaseTask(storageKey, latestTask, resultData);
            if (!isAiTestcaseTaskPending(task.status)
              && !isAiTestcaseTaskCanceled(task.status)
              && !task.notificationShownAt) {
              this.notifyTask(storageKey, task);
            }
            this.$delete(this.polling, cachedTask.taskId);
            this.refreshVisibleTasks();
          })
          .catch(() => {
            this.$delete(this.polling, cachedTask.taskId);
          });
      });
    },
    refreshVisibleTasks(keys) {
      if (!this.isWatcherEnabled()) {
        this.visibleTasks = [];
        return;
      }
      const storageKeys = keys || getAiTestcaseTaskKeys();
      const tasks = [];
      storageKeys.forEach(storageKey => {
        const task = readAiTestcaseTask(storageKey);
        if (!task || !task.taskId || task.cardDismissedAt
          || !isAiTestcaseTaskFinished(task.status)
          || isAiTestcaseTaskCanceled(task.status)) {
          return;
        }
        tasks.push(Object.assign({}, task, { storageKey: storageKey }));
      });
      tasks.sort((left, right) => {
        const leftTime = left.completedAt || left.updatedAt || left.createdAt || 0;
        const rightTime = right.completedAt || right.updatedAt || right.createdAt || 0;
        return rightTime - leftTime;
      });
      this.visibleTasks = tasks.slice(0, 5);
    },
    getTaskProblemLabel(task) {
      return getAiTestcaseTaskProblemLabel(task);
    },
    navigateToTaskPage(task) {
      const routeName = task.routeName || "NewQuestion";
      if (task.contestId) {
        sessionStorage.setItem("contestId", task.contestId);
      }
      if (task.problemId) {
        sessionStorage.setItem("problemId", task.problemId);
      } else {
        sessionStorage.removeItem("problemId");
      }
      if (task.taskId) {
        sessionStorage.setItem(AI_TESTCASE_REVIEW_TASK_ID, task.taskId);
      }
      if (this.$route && this.$route.name === routeName) {
        const reviewTask = Object.assign({}, task, { reviewRequestHandled: false });
        dispatchAiTestcaseReviewRequest(reviewTask);
        this.$nextTick(() => {
          if (reviewTask.reviewRequestHandled) {
            this.dismissTask(task);
          }
        });
        return;
      }
      this.dismissTask(task);
      this.$router.push({ name: routeName }, () => {}, () => {});
    },
    dismissTask(task) {
      dismissAiTestcaseTask(task);
      this.refreshVisibleTasks();
    },
    notifyTask(storageKey, task) {
      markAiTestcaseTaskNotified(storageKey, task);
      this.refreshVisibleTasks();
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-testcase-task-panel {
  position: fixed;
  top: 72px;
  right: 24px;
  z-index: 3000;
  width: 340px;
  max-width: calc(100vw - 48px);
  padding: 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 32px rgba(24, 39, 75, 0.18);
  border: 1px solid #e8f1ff;
}

.ai-testcase-task-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.ai-testcase-task-card {
  position: relative;
  padding: 12px;
  padding-right: 34px;
  border-radius: 10px;
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.ai-testcase-task-card + .ai-testcase-task-card {
  margin-top: 10px;
}

.ai-testcase-task-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(64, 158, 255, 0.16);
}

.ai-testcase-task-card.is-error {
  background: #fef0f0;
  border-color: #fbc4c4;
}

.ai-testcase-task-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #909399;
  font-size: 18px;
  line-height: 20px;
  cursor: pointer;
}

.ai-testcase-task-close:hover {
  background: rgba(144, 147, 153, 0.12);
  color: #606266;
}

.ai-testcase-task-main {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 20px;
}

.ai-testcase-task-status {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  color: #fff;
  background: #67c23a;
  font-size: 12px;
}

.ai-testcase-task-card.is-error .ai-testcase-task-status {
  background: #f56c6c;
}

.ai-testcase-task-problem {
  min-width: 0;
  color: #303133;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-testcase-task-desc {
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
  line-height: 18px;
}
</style>



// WEBPACK FOOTER //
// src/components/question/AiTestcaseTaskNotifier.vue